import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { applyDocumentLang, setLocale, useLocale, type Locale } from '@/i18n';
import {
  effectiveCurrency,
  formatCharge,
  formatDate,
  formatDayLong,
  formatDayMonth,
  formatDayShort,
  formatDayTime,
  formatDays,
  formatFixed,
  formatMoney,
  formatMonthYear,
  formatNumber,
  formatPercent,
  formatRange,
  formatWeekdayDay,
  formatWeekdayShort,
} from '@/lib/format';
import { coerceCurrency, type Currency, type Language } from '@/services/domain';
import { usePlatformConfig } from '@/services/queries';
import { readStored, writeStored } from '@/services/storage';

interface Preferences {
  /** The interface language: French or English (Arabic is hidden, a stored `ar` reads as French). */
  language: Locale;
  /**
   * The effective currency: what prices are shown in and what the card is charged in (posted with bookings
   * and resumes). The choice, unless its rate is missing once the rates are loaded: then MAD (API-FX-1).
   */
  currency: Currency;
  /** The visitor's own choice (the picker), stored under `cardnd.currency` (the old `selectedCurrency` carried over, L5). */
  selectedCurrency: Currency;
  setLanguage: (l: Language) => void;
  setCurrency: (c: Currency) => void;
  /** Formats an amount stored in MAD in the effective currency and the current language. */
  money: (amountMad: number) => string;
}

const Ctx = createContext<Preferences | null>(null);
/** Before the rates are known (or when the call failed): only MAD can be priced. */
const MAD_ONLY = { MAD: 1 } as const;

export function PreferencesProvider({ children }: { children: ReactNode }) {
  // The language lives in the i18n store (`cardnd.lang`), shared with non-React code (services, lib/format).
  const language = useLocale();
  const [selectedCurrency, setCurrencyState] = useState<Currency>(() =>
    coerceCurrency(readStored<unknown>('currency', 'MAD')),
  );
  const { data: config, isPending: ratesLoading } = usePlatformConfig();
  const queryClient = useQueryClient();
  // No indicative rates: while loading the amounts show in MAD, and a missing rate keeps MAD (API-FX-1).
  const rates = config?.rates;
  const currency = effectiveCurrency(selectedCurrency, rates, ratesLoading);

  useEffect(() => {
    applyDocumentLang(language);
  }, [language]);

  const setLanguage = useCallback(
    (l: Language) => {
      const before = language;
      setLocale(l);
      // Texts the services built in the old language (labels, refund tiers, dates) are re-read.
      if (l !== before) void queryClient.invalidateQueries();
    },
    [language, queryClient],
  );
  const setCurrency = useCallback((c: Currency) => {
    setCurrencyState(c);
    writeStored('currency', c);
  }, []);
  const money = useCallback(
    (n: number) => formatMoney(n, currency, rates ?? MAD_ONLY, language),
    [currency, rates, language],
  );

  const value = useMemo(
    () => ({ language, currency, selectedCurrency, setLanguage, setCurrency, money }),
    [language, currency, selectedCurrency, setLanguage, setCurrency, money],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePreferences(): Preferences {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return ctx;
}

/** Shortcut: `const money = useMoney(); money(1196)` → «1 196 MAD» (en «1,196 MAD»). */
// eslint-disable-next-line react-refresh/only-export-components
export const useMoney = () => usePreferences().money;

/**
 * Every display format in the current language and currency; the component re-renders when either changes.
 *   const f = useFormat(); f.money(1196); f.dayShort('2026-11-13'); f.range(start, end); f.days(4)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFormat() {
  const { language: locale, money } = usePreferences();
  return useMemo(
    () => ({
      locale,
      /** MAD amount in the chosen currency: «1 196 MAD» / «1,196 MAD». */
      money,
      /** Amount already in `currency`, 2 decimals for EUR/USD (what the card is charged). */
      charge: (amount: number, currency?: string) => formatCharge(amount, currency, locale),
      /** «ven. 13 nov.» / «Fri 13 Nov» */
      dayShort: (d: string | Date) => formatDayShort(d, locale),
      /** «13 nov.» / «13 Nov» */
      dayMonth: (d: string | Date) => formatDayMonth(d, locale),
      /** «vendredi 13 novembre» / «Friday 13 November» */
      dayLong: (d: string | Date) => formatDayLong(d, locale),
      /** «13 novembre 2026» / «13 November 2026» */
      date: (d: string | Date) => formatDate(d, locale),
      /** «novembre 2026» / «November 2026» */
      monthYear: (d: string | Date) => formatMonthYear(d, locale),
      /** «ven.» / «Fri» */
      weekdayShort: (d: string | Date) => formatWeekdayShort(d, locale),
      /** «ven. 13» / «Fri 13» */
      weekdayDay: (d: string | Date) => formatWeekdayDay(d, locale),
      /** «ven. 13 nov. à 10:00» / «Fri 13 Nov at 10:00» */
      dayTime: (d: string | Date, time: string) => formatDayTime(d, time, locale),
      /** «13 → 17 nov.» / «13 → 17 Nov» */
      range: (start: string, end: string | null) => formatRange(start, end, locale),
      /** «4 jours» / «4 days» */
      days: (n: number) => formatDays(n, locale),
      /** Fixed decimals: «4,0» / «4.0» */
      fixed: (n: number, digits: number) => formatFixed(n, digits, locale),
      /** «4,8» / «4.8» */
      number: (n: number, maxDecimals?: number) => formatNumber(n, maxDecimals, locale),
      /** «10 %» / «10%» */
      percent: (n: number) => formatPercent(n, locale),
    }),
    [locale, money],
  );
}

export type Format = ReturnType<typeof useFormat>;
